import React, { useState, useEffect, useRef, useCallback } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, ActivityIndicator } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { webColors, webBorderRadius, webShadows } from '../../../../constants/webStyles';
import { getCoord } from '../../../../services/shared';
import { COUNTRIES } from '../../../../constants/countries';

// Default coordinates by country (fallback when geocoding fails)
const COUNTRY_DEFAULTS = {
  MY: { lat: 3.1390, lng: 101.6869 },   // Kuala Lumpur
  SG: { lat: 1.3521, lng: 103.8198 },   // Singapore
  PH: { lat: 14.5995, lng: 120.9842 },  // Manila
  ID: { lat: -6.2088, lng: 106.8456 },  // Jakarta
  IN: { lat: 28.6139, lng: 77.2090 },   // New Delhi
  VN: { lat: 21.0285, lng: 105.8542 },  // Hanoi
  KH: { lat: 11.5564, lng: 104.9282 },  // Phnom Penh
  TH: { lat: 13.7563, lng: 100.5018 },  // Bangkok
  LA: { lat: 17.9757, lng: 102.6331 },  // Vientiane
};

const DEFAULT_ZOOM = 17;

const StepLocationWeb = ({ formData, updateFormData, goToStep, isDoctor }) => {
  const [isMapLoading, setIsMapLoading] = useState(true);
  const [isGeocoding, setIsGeocoding] = useState(true);
  const [geocodeError, setGeocodeError] = useState(null);
  const [mapCenter, setMapCenter] = useState(null);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [showGuideModal, setShowGuideModal] = useState(true);

  const mapContainerRef = useRef(null);
  const mapRef = useRef(null);

  // Build full address string from form data
  const buildAddressString = useCallback(() => {
    const countryName = COUNTRIES[formData.country]?.name || 'Malaysia';
    const parts = [
      formData.address,
      formData.city,
      formData.postalCode,
      formData.state,
      countryName,
    ].filter(Boolean);
    return parts.join(', ');
  }, [formData.address, formData.city, formData.postalCode, formData.state, formData.country]);

  // Get default coordinates for country
  const getDefaultCoords = useCallback(() => {
    return COUNTRY_DEFAULTS[formData.country] || COUNTRY_DEFAULTS.MY;
  }, [formData.country]);

  // Geocode the address on mount
  useEffect(() => {
    let isCancelled = false;

    const geocodeAddress = async () => {
      setIsGeocoding(true);
      setGeocodeError(null);

      try {
        const addressString = buildAddressString();
        const response = await getCoord(addressString);

        if (isCancelled) return; // Don't update state if unmounted

        const coords = response.data?.data;

        if (coords?.latitude && coords?.longitude) {
          const lat = parseFloat(coords.latitude);
          const lng = parseFloat(coords.longitude);

          // Validate coordinates are valid numbers
          if (!isNaN(lat) && !isNaN(lng)) {
            const center = { lat, lng };
            setMapCenter(center);
            setSelectedLocation({
              latitude: lat,
              longitude: lng,
            });
          } else {
            throw new Error('Invalid coordinates received');
          }
        } else {
          // Fallback to country default
          const defaultCoords = getDefaultCoords();
          setMapCenter(defaultCoords);
          setSelectedLocation({
            latitude: defaultCoords.lat,
            longitude: defaultCoords.lng,
          });
          setGeocodeError('Could not find exact address. Please adjust the pin to your facility location.');
        }
      } catch (error) {
        if (isCancelled) return; // Don't update state if unmounted

        console.error('Geocoding error:', error);
        // Fallback to country default
        const defaultCoords = getDefaultCoords();
        setMapCenter(defaultCoords);
        setSelectedLocation({
          latitude: defaultCoords.lat,
          longitude: defaultCoords.lng,
        });
        setGeocodeError('Could not geocode address. Please position the pin manually on your facility location.');
      } finally {
        if (!isCancelled) {
          setIsGeocoding(false);
        }
      }
    };

    geocodeAddress();

    return () => {
      isCancelled = true;
    };
  }, [buildAddressString, getDefaultCoords]);

  // Initialize Google Map once geocoding is complete
  useEffect(() => {
    if (!mapCenter || !mapContainerRef.current || isGeocoding) return;

    let isMounted = true;

    // Check if Google Maps API is loaded
    if (!window.google || !window.google.maps) {
      // Allow user to proceed with geocoded coordinates if Maps doesn't load
      setGeocodeError('Google Maps could not be loaded. You can continue with the geocoded location, or refresh the page to try again.');
      setIsMapLoading(false);
      return;
    }

    // Create the map
    const map = new window.google.maps.Map(mapContainerRef.current, {
      center: mapCenter,
      zoom: DEFAULT_ZOOM,
      disableDefaultUI: true,
      zoomControl: true,
      fullscreenControl: false,
      streetViewControl: false,
      mapTypeControl: false,
      gestureHandling: 'greedy',
    });

    mapRef.current = map;

    // Update selected location when map center changes (on drag)
    map.addListener('center_changed', () => {
      if (!isMounted) return; // Guard against unmounted updates

      const center = map.getCenter();
      if (center) {
        const lat = center.lat();
        const lng = center.lng();

        // Validate coordinates are valid numbers
        if (typeof lat === 'number' && typeof lng === 'number' && !isNaN(lat) && !isNaN(lng)) {
          setSelectedLocation({
            latitude: lat,
            longitude: lng,
          });
        }
      }
    });

    setIsMapLoading(false);

    // Cleanup
    return () => {
      isMounted = false;
      if (mapRef.current) {
        window.google.maps.event.clearInstanceListeners(mapRef.current);
      }
    };
  }, [mapCenter, isGeocoding]);

  // Handle continue button
  const handleContinue = () => {
    if (selectedLocation) {
      updateFormData('latitude', selectedLocation.latitude);
      updateFormData('longitude', selectedLocation.longitude);
      goToStep(5); // Go to Verify step
    }
  };

  // Determine if we can continue
  const canContinue = selectedLocation && !isMapLoading && !isGeocoding;

  return (
    <View style={styles.container}>
      {/* Header */}
      <View style={styles.stepHeader}>
        <Text style={styles.stepIcon}>📍</Text>
        <Text style={styles.stepTitle}>Facility Location</Text>
        <Text style={styles.stepSubtitle}>
          Drag the map to mark your facility's exact location
        </Text>
      </View>

      {/* Geocode Error Warning */}
      {geocodeError && (
        <View style={styles.warningBox}>
          <Text style={styles.warningText}>{geocodeError}</Text>
        </View>
      )}

      {/* Address Display */}
      <View style={styles.addressCard}>
        <View style={styles.addressIconContainer}>
          <Ionicons name="location-outline" size={20} color={webColors.textSecondary} />
        </View>
        <View style={styles.addressTextContainer}>
          <Text style={styles.addressLabel}>Address</Text>
          <Text style={styles.addressText}>{buildAddressString()}</Text>
        </View>
        <TouchableOpacity style={styles.editButton} onPress={() => goToStep(3)}>
          <Text style={styles.editButtonText}>Edit</Text>
        </TouchableOpacity>
      </View>

      {/* Map Container */}
      <View style={styles.mapWrapper}>
        {/* Loading Overlay */}
        {(isGeocoding || isMapLoading) && (
          <View style={styles.loadingOverlay}>
            <ActivityIndicator size="large" color={webColors.primary} />
            <Text style={styles.loadingText}>
              {isGeocoding ? 'Finding your location...' : 'Loading map...'}
            </Text>
          </View>
        )}

        {/* Map */}
        <div
          ref={mapContainerRef}
          style={{
            width: '100%',
            height: '100%',
            borderRadius: 12,
          }}
        />

        {/* Fixed Pin Overlay */}
        {!isGeocoding && !isMapLoading && (
          <View style={styles.pinOverlay}>
            <View style={styles.pinContainer}>
              <Ionicons
                name="location-sharp"
                size={55}
                color="#d40b1b"
                style={styles.pinIcon}
              />
            </View>
          </View>
        )}
      </View>

      {/* Guide Modal */}
      {showGuideModal && !isGeocoding && !isMapLoading && (
        <View style={styles.guideModal}>
          <View style={styles.guideContent}>
            <View style={styles.guideIconContainer}>
              <Ionicons name="information-circle-outline" size={24} color={webColors.primary} />
            </View>
            <Text style={styles.guideTitle}>Adjust Pin Location</Text>
            <Text style={styles.guideText}>
              Please drag the map to position the pin exactly on your facility's location. This helps doctors find your clinic accurately.
            </Text>
            <TouchableOpacity
              style={styles.guideButton}
              onPress={() => setShowGuideModal(false)}
            >
              <Text style={styles.guideButtonText}>Got it</Text>
            </TouchableOpacity>
          </View>
        </View>
      )}

      {/* Instruction Box */}
      <View style={styles.instructionBox}>
        <Ionicons name="hand-left-outline" size={18} color={webColors.primary} />
        <Text style={styles.instructionText}>
          Drag the map to position the pin on your facility's exact location
        </Text>
      </View>

      {/* Navigation Buttons */}
      <View style={styles.btnRow}>
        <TouchableOpacity
          style={styles.btnBack}
          onPress={() => goToStep(3)}
        >
          <Text style={styles.btnBackText}>Back</Text>
        </TouchableOpacity>
        <TouchableOpacity
          style={[styles.btnNext, !canContinue && styles.btnNextDisabled]}
          onPress={handleContinue}
          disabled={!canContinue}
        >
          <Text style={[styles.btnNextText, !canContinue && styles.btnNextTextDisabled]}>
            {isGeocoding ? 'Finding...' : isMapLoading ? 'Loading...' : 'Continue'}
          </Text>
        </TouchableOpacity>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  stepHeader: {
    marginBottom: 24,
  },
  stepIcon: {
    fontSize: 48,
    marginBottom: 16,
  },
  stepTitle: {
    fontSize: 28,
    fontWeight: '700',
    color: webColors.textPrimary,
    marginBottom: 8,
  },
  stepSubtitle: {
    fontSize: 16,
    color: webColors.primary,
  },
  warningBox: {
    backgroundColor: webColors.warningLight,
    padding: 12,
    borderRadius: webBorderRadius.small,
    marginBottom: 16,
  },
  warningText: {
    color: webColors.warning,
    fontSize: 14,
  },
  addressCard: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: webColors.backgroundAlt,
    padding: 14,
    borderRadius: webBorderRadius.medium,
    marginBottom: 16,
    borderWidth: 1,
    borderColor: webColors.border,
  },
  addressIconContainer: {
    marginRight: 12,
  },
  addressTextContainer: {
    flex: 1,
  },
  addressLabel: {
    fontSize: 12,
    color: webColors.textMuted,
    marginBottom: 2,
  },
  addressText: {
    fontSize: 14,
    color: webColors.textPrimary,
    lineHeight: 20,
  },
  editButton: {
    paddingHorizontal: 12,
    paddingVertical: 6,
  },
  editButtonText: {
    color: webColors.primary,
    fontSize: 14,
    fontWeight: '600',
  },
  mapWrapper: {
    position: 'relative',
    width: '100%',
    height: 350,
    borderRadius: webBorderRadius.large,
    overflow: 'hidden',
    marginBottom: 16,
    borderWidth: 2,
    borderColor: webColors.border,
  },
  loadingOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: webColors.backgroundAlt,
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 10,
  },
  loadingText: {
    marginTop: 12,
    fontSize: 14,
    color: webColors.textSecondary,
  },
  pinOverlay: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: [{ translateX: -27.5 }, { translateY: -55 }],
    zIndex: 5,
    pointerEvents: 'none',
  },
  pinContainer: {
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.3,
    shadowRadius: 6,
  },
  pinIcon: {
    // Additional styling if needed
  },
  guideModal: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 20,
    padding: 20,
  },
  guideContent: {
    backgroundColor: webColors.cardBackground,
    borderRadius: webBorderRadius.large,
    padding: 24,
    maxWidth: 360,
    width: '100%',
    alignItems: 'center',
    ...webShadows.modal,
  },
  guideIconContainer: {
    marginBottom: 12,
  },
  guideTitle: {
    fontSize: 18,
    fontWeight: '700',
    color: webColors.textPrimary,
    marginBottom: 8,
    textAlign: 'center',
  },
  guideText: {
    fontSize: 14,
    color: webColors.textSecondary,
    textAlign: 'center',
    lineHeight: 22,
    marginBottom: 20,
  },
  guideButton: {
    backgroundColor: webColors.primary,
    paddingHorizontal: 32,
    paddingVertical: 12,
    borderRadius: webBorderRadius.medium,
    ...webShadows.button,
  },
  guideButtonText: {
    color: '#ffffff',
    fontSize: 15,
    fontWeight: '600',
  },
  instructionBox: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: webColors.primaryLight,
    padding: 12,
    borderRadius: webBorderRadius.small,
    marginBottom: 24,
    gap: 10,
  },
  instructionText: {
    flex: 1,
    fontSize: 14,
    color: webColors.primary,
    lineHeight: 20,
  },
  btnRow: {
    flexDirection: 'row',
    gap: 16,
  },
  btnBack: {
    flex: 1,
    backgroundColor: webColors.backgroundAlt,
    paddingVertical: 16,
    borderRadius: webBorderRadius.medium,
    alignItems: 'center',
  },
  btnBackText: {
    color: webColors.textSecondary,
    fontSize: 16,
    fontWeight: '600',
  },
  btnNext: {
    flex: 2,
    backgroundColor: webColors.primary,
    paddingVertical: 16,
    borderRadius: webBorderRadius.medium,
    alignItems: 'center',
    ...webShadows.button,
  },
  btnNextDisabled: {
    backgroundColor: webColors.border,
    shadowOpacity: 0,
  },
  btnNextText: {
    color: '#ffffff',
    fontSize: 16,
    fontWeight: '600',
  },
  btnNextTextDisabled: {
    color: webColors.textMuted,
  },
});

export default StepLocationWeb;
